import { memo } from 'react'

import { motion } from 'framer-motion'
import { CloseIcon } from '@chakra-ui/icons'
import { Box, IconButton, Spinner, Text, useDisclosure } from '@chakra-ui/react'

import { Interviewer } from '@pkg/types/interview'

import { useInterview } from '@/react-query/interview/interview'
import { useRemoveInterviewParticipant } from '@/react-query/interview/interview-participants'

import { DeleteInterviewerModal } from './components/DeleteInterviewerModal'
import { InterviewerAvatar } from './components/InterviewerAvatar'

interface InterviewerItemProps {
  interviewer: Interviewer
}

export const InterviewerItem = memo(({ interviewer }: InterviewerItemProps) => {
  const { data: interview } = useInterview()
  const removeParticipant = useRemoveInterviewParticipant()
  const { isOpen, onOpen, onClose } = useDisclosure()

  // Can only delete if there's more than one interviewer
  const canDelete = (interview?.interviewers?.length ?? 0) > 1 && interviewer.id

  // Check if this specific interviewer is being deleted
  const isDeleting =
    removeParticipant.isPending &&
    removeParticipant.variables?.userId === interviewer.id

  const handleConfirmDelete = () => {
    if (!interviewer.id) {
      console.error('Interviewer ID is required for deletion')
      return
    }

    removeParticipant.mutate({ userId: interviewer.id })
    onClose()
  }

  return (
    <>
      <motion.div
        layout
        initial={{ opacity: 0, height: 0 }}
        animate={{
          opacity: 1,
          height: 'auto',
        }}
        exit={{
          opacity: 0,
          height: 0,
        }}
        transition={{
          opacity: { duration: 0.2 },
          height: { duration: 0.2 },
        }}
        style={{ overflow: 'hidden' }}
      >
        <div className="flex items-center justify-between mt-6">
          <Box className="flex items-center">
            <InterviewerAvatar
              photoURL={interviewer.photoURL}
              displayName={interviewer.displayName}
            />
            <div className="ml-3">
              <Text fontWeight={'bold'} fontSize={'1.25rem'}>
                {interviewer.displayName}
              </Text>
            </div>
          </Box>
          {canDelete ? (
            <IconButton
              variant={'brand-solid-white'}
              rounded={'full'}
              aria-label="Remove Interviewer"
              icon={isDeleting ? <Spinner size="sm" /> : <CloseIcon />}
              onClick={onOpen}
              isDisabled={isDeleting}
            />
          ) : (
            <Box width="40px" height="40px" />
          )}
        </div>
      </motion.div>

      <DeleteInterviewerModal
        isOpen={isOpen}
        onClose={onClose}
        onConfirm={handleConfirmDelete}
        interviewerName={interviewer.displayName}
      />
    </>
  )
})

InterviewerItem.displayName = 'InterviewerItem'
